import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Heart, ArrowLeft, MessageCircle, CheckCircle2, Sparkles } from "lucide-react";

const CATEGORIES = [
  { value: "feedback", label: "Share feedback" },
  { value: "bug", label: "Report a bug" },
  { value: "question", label: "Ask a question" },
  { value: "prayer", label: "Prayer request" },
  { value: "other", label: "Something else" },
];

export default function Contact() {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [category, setCategory] = useState("feedback");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setSending(true);
    try {
      await api.post("/contact", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        category,
        message: message.trim(),
      });
      setSent(true);
    } catch (err) {
      toast.error("Couldn't send just now — please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grain-bg min-h-screen relative overflow-hidden" data-testid="contact-page">
      <div className="absolute -top-40 -left-24 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl" style={{ background: "radial-gradient(circle, #D4C7E1 0%, transparent 60%)" }} />
      <div className="absolute -bottom-40 -right-16 w-[520px] h-[520px] rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, #EFEBF4 0%, transparent 60%)" }} />

      <header className="relative z-10 border-b border-[#EFEBF4]/60">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="contact-brand" className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: "#9B8BAF" }}>
              <Heart className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
            </div>
            <span className="text-base tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              My Daily Prayer
            </span>
          </Link>
          <Link
            to={user ? "/dashboard" : "/"}
            data-testid="contact-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-[#75737A] hover:text-[#2C2A2E] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {user ? "Back to journal" : "Back to home"}
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] bg-[#EFEBF4] text-[#75737A] border border-[#D4C7E1]">
            <MessageCircle className="h-3 w-3" /> Contact
          </div>
          <h1
            className="mt-6 text-5xl sm:text-6xl leading-[1.05] text-[#2C2A2E]"
            data-testid="contact-title"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            We'd love
            <br />
            <span className="italic text-[#9B8BAF]">to hear from you.</span>
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-[#75737A]">
            Whether you've found a bug, want to share how the journal has blessed
            you, or have a request on your heart — we read every message.
          </p>
          <p
            className="mt-4 text-sm text-[#9B8BAF] italic"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            &ldquo;A word fitly spoken is like apples of gold in settings of silver.&rdquo;
            <span className="not-italic text-[#75737A] text-xs tracking-wide"> — Proverbs 25:11</span>
          </p>
        </div>

        <section className="mt-12">
          {sent ? (
            <div
              className="rounded-2xl bg-white border border-[#D4C7E1] p-8 lg:p-10"
              data-testid="contact-sent-state"
            >
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-full bg-[#EFEBF4] flex items-center justify-center shrink-0">
                  <CheckCircle2 className="h-5 w-5 text-[#9B8BAF]" />
                </div>
                <div>
                  <h2
                    className="text-2xl text-[#2C2A2E]"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Thank you — your message is on its way
                  </h2>
                  <p className="mt-3 text-[#75737A] leading-relaxed">
                    We'll read it soon and reply to <b className="text-[#2C2A2E]">{email}</b> if a response is needed.
                    Grateful you took a moment to reach out.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link
                      to={user ? "/dashboard" : "/"}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#D4C7E1] px-4 py-2 text-sm text-[#2C2A2E] hover:bg-[#EFEBF4] transition-colors"
                      data-testid="contact-return-link"
                    >
                      {user ? "Return to journal" : "Return home"}
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setSent(false);
                        setMessage("");
                      }}
                      className="text-sm text-[#9B8BAF] hover:text-[#8A799E] underline underline-offset-4"
                      data-testid="contact-send-another"
                    >
                      Send another message
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="rounded-2xl bg-white border border-[#D4C7E1] p-8 lg:p-10 space-y-6"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label htmlFor="contact-name" className="text-xs uppercase tracking-[0.18em] text-[#75737A]">
                    Your name
                  </Label>
                  <Input
                    id="contact-name"
                    data-testid="contact-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Ruth"
                    maxLength={100}
                    required
                    className="bg-[#FAF9F6] border-[#D4C7E1] rounded-xl h-11"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-email" className="text-xs uppercase tracking-[0.18em] text-[#75737A]">
                    Your email
                  </Label>
                  <Input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="bg-[#FAF9F6] border-[#D4C7E1] rounded-xl h-11"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.18em] text-[#75737A]">
                  What's this about?
                </Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger
                    data-testid="contact-category-select"
                    className="bg-[#FAF9F6] border-[#D4C7E1] rounded-xl h-11"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#D4C7E1]">
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value} data-testid={`contact-cat-${c.value}`}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="contact-message" className="text-xs uppercase tracking-[0.18em] text-[#75737A]">
                  Your message
                </Label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Tell us what's on your heart…"
                  rows={6}
                  maxLength={5000}
                  required
                  className="bg-[#FAF9F6] border-[#D4C7E1] rounded-xl resize-none"
                />
                <div className="text-right text-[10px] text-[#9B8BAF]/70">
                  {message.length} / 5000
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between pt-2">
                <p className="text-xs text-[#75737A] flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-[#9B8BAF]" />
                  We treat every note with care and privacy.
                </p>
                <Button
                  type="submit"
                  disabled={sending || !name.trim() || !email.trim() || !message.trim()}
                  className="btn-primary rounded-full h-11 px-7"
                  data-testid="contact-submit-btn"
                >
                  {sending ? "Sending…" : "Send message"}
                </Button>
              </div>
            </form>
          )}
        </section>

        <div className="mt-12 pt-8 border-t border-[#EFEBF4] flex flex-col md:flex-row gap-3 md:items-center md:justify-end text-xs text-[#75737A]">
          <Link to="/support" className="text-[#9B8BAF] hover:text-[#8A799E] underline underline-offset-4">
            Or support the journal →
          </Link>
        </div>
      </main>
    </div>
  );
}
